import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import type { SubjectFetchFailedAction } from '@Application/Subjects/Actions/subjectFetchFailed';
import FetchSubject from '@Application/Subjects/Queries/FetchSubject';
import fetchSubject from '@Application/Subjects/fetchSubject';
import { PermissionEnum } from '@Application/Security/Roles';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { reveal } from '@Guidelines/Theme/helpers/animations';
import ErrorState from '@Guidelines/Component/ErrorState';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import CompletedPreparationsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/CompletedPreparationsSection';
import OpenPreparationsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/OpenPreparationsSection';
import PendingDocumentsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/PendingDocumentsSection';
import PendingRecordingsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/PendingRecordingsSection';
import PreparationsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/PreparationsNotifications';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import SubjectActionsRow from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectActionsRow';
import SubjectDetailsSkeleton from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectDetailsSkeleton';
import SubjectReadingColumn from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectReadingColumn';
import SubjectArchivalConfirmationDialog from '@UI/Pages/Subjects/SubjectDetails/ConfirmationDialogs/SubjectArchivalConfirmationDialog';
import SubjectDangerZone from '@UI/Pages/Subjects/SubjectDetails/SubjectDangerZone';
import SubjectInformation from '@UI/Pages/Subjects/SubjectDetails/SubjectInformation';
import EditSubjectSidebar from '@UI/Pages/Subjects/Sidebars/EditSubjectSidebar';
import SubjectHeader from '@UI/Pages/Subjects/SubjectDetails/SubjectHeader';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type SubjectTypeView from '@UI/Store/SubjectTypes/Views/SubjectTypeView';
import { Plus, Users } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

/**
 * Detects a `SUBJECT_FETCH_FAILED` rejection carrying a `404`. `dispatch(...)` rejects with the failed
 * action itself, whose status `useStoreQuery` would lose by re-wrapping it into a generic `Error`.
 */
const isSubjectNotFoundFailure = (candidate: unknown): candidate is SubjectFetchFailedAction => {
	if (typeof candidate !== 'object' || candidate === null) {
		return false;
	}

	const action = candidate as Partial<SubjectFetchFailedAction>;

	return action.type === 'SUBJECT_FETCH_FAILED' && action.payload?.status === 404;
};

type Props = {
	readonly subject: SubjectAddress;
	readonly type: SubjectTypeView;
};

/**
 * Owns the fetch and every loading/error/not-found/nominal branch of a dossier of a configurable type.
 * The identity banner, the sheet panel it reveals and the beneficiary's autonomous sections stack in
 * one reading column; each section loads what it draws.
 *
 * The dossier is left for the list of its type when the server answers `404`, or when it belongs to
 * another type than the route's: that renders `null` first, so no error flashes before the redirect.
 * `error !== null` comes before the data-absent branch, never the reverse (`guides/state-stores.md`).
 */
const SubjectDetailsContent: React.FC<Props> = ({ subject, type }) => {
	const subjectId = subject.subjectId;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const listPath = buildSubjectListPath(type.key);

	const [notFound, setNotFound] = React.useState(false);
	const [isInfoOpen, setIsInfoOpen] = React.useState(false);
	const infoPanelId = React.useId();

	const {
		data: loadedSubject,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.subjectsStore.subject,
		stopOn: (value) => value !== null && value.id === subjectId,
		query: () =>
			dispatch(fetchSubject(new FetchSubject(facilityId, subjectId))).catch((rejection: unknown) => {
				if (isSubjectNotFoundFailure(rejection)) {
					setNotFound(true);
				}

				throw rejection;
			}),
		deps: [subjectId, facilityId],
	});

	// The store keeps the last dossier read: it may be another one while this one loads.
	const currentSubject = loadedSubject !== null && loadedSubject.id === subjectId ? loadedSubject : null;
	const isOfAnotherType = currentSubject !== null && currentSubject.typeId !== type.id;
	const mustLeave = notFound || isOfAnotherType;

	React.useEffect(() => {
		if (mustLeave) {
			navigate(listPath);
		}
	}, [mustLeave, navigate, listPath]);

	const handleStartDocumentsClick = React.useCallback(() => {
		navigate(`${buildSubjectPath(subject)}/preparations/new`);
	}, [subject, navigate]);

	const handleToggleInfo = React.useCallback(() => {
		setIsInfoOpen((previous) => !previous);
	}, []);

	const handleMembersClick = React.useCallback(() => {
		navigate(`${buildSubjectPath(subject)}/members`);
	}, [subject, navigate]);

	if (mustLeave) {
		return null;
	}

	if (error !== null) {
		return <ErrorState title="Impossible de charger le dossier" onRetry={refetch} />;
	}

	if (currentSubject === null) {
		return <SubjectDetailsSkeleton />;
	}

	const sheetTitle = type.fields.length > 0 ? type.wording.sheetTitle : null;

	return (
		<SubjectReadingColumn>
			<SubjectHeader subject={currentSubject} type={type} facilityName={currentFacility.name}>
				<SubjectActionsRow>
					<ConditionalLoader data={currentSubject.canModify}>
						<WithPermission
							permission={[
								PermissionEnum.INTERVIEW_WRITE,
								PermissionEnum.DOCUMENT_GENERATION_LAUNCH,
							]}
							mode="any"
						>
							<Button
								type="button"
								size="lg"
								icon={<Plus size={16} />}
								label="Commencer des documents"
								onClick={handleStartDocumentsClick}
							/>
						</WithPermission>
					</ConditionalLoader>
					<ConditionalLoader data={sheetTitle}>
						{(title) => (
							<Button
								type="button"
								severity="secondary"
								aria-expanded={isInfoOpen}
								aria-controls={infoPanelId}
								label={isInfoOpen ? 'Masquer les informations' : title}
								onClick={handleToggleInfo}
							/>
						)}
					</ConditionalLoader>
					<Button
						type="button"
						severity="secondary"
						icon={<Users size={16} />}
						label="Membres"
						onClick={handleMembersClick}
					/>
				</SubjectActionsRow>
			</SubjectHeader>

			<ConditionalLoader data={isInfoOpen ? sheetTitle : null}>
				{(title) => (
					<div id={infoPanelId} css={reveal(0)}>
						<SubjectInformation subject={currentSubject} type={type} title={title} />
					</div>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={currentSubject.canModify && sheetTitle !== null}>
				<EditSubjectSidebar subject={currentSubject} type={type} />
			</ConditionalLoader>

			<OpenPreparationsSection
				subject={subject}
				isArchived={currentSubject.isArchived || !currentSubject.canWrite}
			/>
			<PendingDocumentsSection subject={subject} />
			<CompletedPreparationsSection subject={subject} />
			<ConditionalLoader data={currentSubject.canModify}>
				<PendingRecordingsSection subject={subject} />
			</ConditionalLoader>
			<ConditionalLoader data={currentSubject.canWrite}>
				<SubjectDangerZone subjectId={currentSubject.id} isArchived={currentSubject.isArchived} />
			</ConditionalLoader>
			<SubjectArchivalConfirmationDialog facilityId={facilityId} typeKey={subject.typeKey} />
			<PreparationsNotifications />
		</SubjectReadingColumn>
	);
};

export default SubjectDetailsContent;
